import { AnyAction, Dispatch } from '@reduxjs/toolkit';
import { IGuide, KeyCodeTypes } from 'common/interfaces';
import useAddEventListener from 'hooks/useAddEventListener';
import { useCallback, useEffect, useMemo } from 'react';
import KeyboardService from 'services/KeyboardService';
import {
  addGuideToSelected,
  clearSelectedGuidesIds,
  selectGuidesByIds,
} from 'store/reducers/guides.slice';

export default function useGuideSelection(
  guideData: IGuide,
  selectedGuidesIds: string[],
  elemRef: React.RefObject<HTMLDivElement>,
  pressedKeys: string[],
  dispatch: Dispatch<AnyAction>,
): { isSelected: boolean } {
  const isSelected = useMemo((): boolean => {
    return selectedGuidesIds.includes(guideData.id);
  }, [selectedGuidesIds, guideData.id]);

  const selectGuide = useCallback((): void => {
    if (
      // guideData.isBlocked ||
      isSelected ||
      KeyboardService.checkIfPressedOneKeyOf(pressedKeys, [KeyCodeTypes.SPACE])
    ) {
      return;
    }

    if (
      KeyboardService.checkIfPressedOneKeyOf(pressedKeys, [
        KeyCodeTypes.SHIFT_LEFT,
        KeyCodeTypes.SHIFT_RIGHT,
      ])
    ) {
      dispatch(addGuideToSelected(guideData.id));
    } else {
      dispatch(selectGuidesByIds([guideData.id]));
    }
  }, [guideData, dispatch, isSelected, pressedKeys]);

  const removeGuideSelection = useCallback((): void => {
    if (
      KeyboardService.checkIfPressedOneKeyOf(pressedKeys, [
        KeyCodeTypes.SHIFT_LEFT,
        KeyCodeTypes.SHIFT_RIGHT,
        KeyCodeTypes.SPACE,
      ])
    ) {
      return;
    }

    dispatch(clearSelectedGuidesIds());
  }, [dispatch, pressedKeys]);

  useAddEventListener(elemRef, 'click', selectGuide);

  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent): void => {
      const target = event.target as HTMLElement;

      if (target.hasAttribute('data-guide-drag-target')) {
        return;
      }

      removeGuideSelection();
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [removeGuideSelection]);

  return { isSelected };
}
